import React, { useMemo } from 'react';

import { TableCell, TableRow, TableRowActionsMenu } from '@occc/ui-design-system';
import { KioskDefaultScreenFragment } from '@occc/graphql';

import { ThumbnailImage } from '../../../components/thumbnail-image';
import { TABLE_CELL_TRUNCATE_STYLES, TABLE_CELL_IMAGE_STYLES } from '../../../touchpoint.constants';

type DefaultScreensListTableRowProps = {
  defaultScreen: KioskDefaultScreenFragment;
  actions: {
    onItemClick: (defaultScreenId: string) => void;
    onEdit: (defaultScreenId: string) => void;
    onDelete: (defaultScreenId: string) => void;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };
};

export const DefaultScreensListTableRow = ({
  defaultScreen,
  actions,
  permissions,
}: DefaultScreensListTableRowProps) => {
  const { id, name, description, imageSet } = defaultScreen;

  const thumbnailImageUrl = imageSet?.square;

  const menuItems = useMemo(() => {
    const items = [];

    if (permissions.canEdit) {
      items.push({
        label: 'Edit',
        onClick: () => actions.onEdit(id),
        dataTestId: 'edit',
      });
    }

    if (permissions.canDelete) {
      items.push({
        label: 'Delete',
        onClick: () => actions.onDelete(id),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [actions, permissions, id]);

  const isTableRowActionsMenuVisible = permissions.canEdit || permissions.canDelete;

  const onRowClick = () => {
    actions.onItemClick(id);
  };

  return (
    <TableRow onClick={onRowClick}>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{name || ''}</TableCell>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{description || ''}</TableCell>
      <TableCell sx={TABLE_CELL_IMAGE_STYLES}>
        <ThumbnailImage
          imageUrl={thumbnailImageUrl}
          altText={name || 'Default Screen thumbnail'}
          size={40}
        />
      </TableCell>
      {isTableRowActionsMenuVisible && (
        <TableCell padding='button' align='right'>
          <TableRowActionsMenu menuItems={menuItems} />
        </TableCell>
      )}
    </TableRow>
  );
};
